import { Markdown } from '@tiptap/markdown';
import { useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { CharacterCount } from '@tiptap/extension-character-count';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Underline } from '@tiptap/extension-underline';
import { cn } from '@/lib/cn';
import HardBreak from '@tiptap/extension-hard-break';
import { useEffect, useRef } from 'react';

interface UseMarkdownTiptapProps {
  value: string;
  onUpdate?: (markdown: string) => void;
  onBlur?: (markdown: string) => void;
  placeholder?: string;
  characterLimit?: number;
  editorClassName?: string;
  editable?: boolean;
}

export const useMarkdownTiptap = ({
  value,
  onUpdate,
  onBlur,
  placeholder = '',
  characterLimit = 3000,
  editorClassName,
  editable = true,
}: UseMarkdownTiptapProps) => {
  const isInternalChange = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        hardBreak: false,
        link: {
          HTMLAttributes: {
            style: 'text-decoration: underline;',
          },
          openOnClick: false,
          enableClickSelection: true,
          linkOnPaste: true,
        },
        bulletList: {
          HTMLAttributes: {
            style: 'list-style-type: disc; padding-left: 1.5rem;',
          },
        },
        orderedList: {
          HTMLAttributes: {
            style: 'list-style-type: decimal; padding-left: 1.5rem;',
          },
        },
      }),
      HardBreak.extend({
        addKeyboardShortcuts() {
          return {
            'Shift-Enter': () => {
              for (const level of [1, 2, 3] as const) {
                if (this.editor.isActive('heading', { level })) {
                  return this.editor
                    .chain()
                    .splitBlock()
                    .setHeading({ level })
                    .run();
                }
              }
              return this.editor.commands.setHardBreak();
            },
            Enter: () => {
              if (this.editor.isActive('listItem')) {
                return false;
              }
              return this.editor.commands.splitBlock();
            },
          };
        },
      }),
      Underline,
      CharacterCount.configure({ limit: characterLimit }),
      Placeholder.configure({ placeholder }),
      Markdown,
    ],
    content: value,
    contentType: 'markdown',
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    editable: editable,
    editorProps: {
      attributes: {
        class: cn('focus:outline-none', editorClassName),
      },
    },
    onUpdate: ({ editor }) => {
      isInternalChange.current = true;
      const markdown = editor.getMarkdown();
      onUpdate?.(markdown);
    },
    onBlur: ({ editor }) => {
      const markdown = editor.getMarkdown();
      onBlur?.(markdown);
    },
  });

  useEffect(() => {
    if (!editor) return;

    // Skip if the change came from the editor itself
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    // Only update if the content is actually different
    const currentMarkdown = editor.getMarkdown() ?? '';
    if (value !== currentMarkdown) {
      editor.commands.setContent(value, {
        emitUpdate: false,
        contentType: 'markdown',
      });
    }
  }, [value, editor]);

  return editor;
};
